import type { ReactNode } from 'react';
import { Badge, Group, Paper, Stack, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';

/**
 * Bausteine der Kundeninfos. Jede Karte hat denselben Kopf — Symbol, Titel, Anzahl,
 * Erklärung, Aktionen —, damit man eine Stelle wiederfindet, statt jede Box neu zu
 * lesen. Erklärtexte stehen hinter dem Info-Symbol: zwischen den Feldern verdrängen
 * sie den eigentlichen Inhalt.
 */

export function HintIcon({ label, width = 280 }: { label: ReactNode; width?: number }) {
  return (
    <Tooltip label={label} multiline w={width}>
      <IconInfoCircle
        size={14}
        aria-hidden
        style={{ color: 'var(--mantine-color-dimmed)', cursor: 'help', flexShrink: 0 }}
      />
    </Tooltip>
  );
}

/** Feldbeschriftung mit Info-Symbol. */
export function LabelWithHint({ label, hint }: { label: string; hint: ReactNode }) {
  return (
    <Group gap={4} wrap="nowrap" component="span" style={{ display: 'inline-flex' }}>
      <span>{label}</span>
      <HintIcon label={hint} />
    </Group>
  );
}

export function InfoCard({
  icon,
  color = 'gray',
  title,
  count,
  hint,
  actions,
  children,
  flush = false,
}: {
  icon: ReactNode;
  color?: string;
  title: ReactNode;
  count?: number | null;
  hint?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  /** Inhalt ohne Innenabstand — für Listen, deren Zeilen bis an den Rand reichen. */
  flush?: boolean;
}) {
  return (
    <Paper
      withBorder
      radius="md"
      style={{ backgroundColor: 'var(--mantine-color-default)', overflow: 'hidden' }}
    >
      <Group justify="space-between" wrap="nowrap" gap="xs" px="md" py="sm">
        <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
          <ThemeIcon size={26} radius="md" variant="light" color={color}>
            {icon}
          </ThemeIcon>
          <Text fw={600} size="sm" truncate>
            {title}
          </Text>
          {count != null && (
            <Badge size="sm" variant="default" radius="sm">
              {count}
            </Badge>
          )}
          {hint && <HintIcon label={hint} />}
        </Group>
        {actions && (
          <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
            {actions}
          </Group>
        )}
      </Group>
      <div
        style={
          flush
            ? { borderTop: '1px solid var(--mantine-color-default-border)' }
            : { padding: '0 var(--mantine-spacing-md) var(--mantine-spacing-md)' }
        }
      >
        {children}
      </div>
    </Paper>
  );
}

/** Leerer Zustand in einer Karte oder einem Reiter — mit optionaler Aktion. */
export function InfoEmpty({ icon, children, action }: { icon: ReactNode; children: ReactNode; action?: ReactNode }) {
  return (
    <Stack align="center" gap={8} py="xl" px="md">
      <ThemeIcon size={40} radius="xl" variant="light" color="gray">
        {icon}
      </ThemeIcon>
      <Text size="sm" c="dimmed" ta="center" maw={420}>
        {children}
      </Text>
      {action}
    </Stack>
  );
}
